
import { apiClient } from '@/lib/apiClient';
import { apiConfig } from '@/lib/env';
import { requestDeduplication } from '@/services/requestDeduplication';
import {
  DraftSaveResponse,
  EmailActionResponse,
  EmailFolderSummary,
  EmailFolderName,
  EmailMessageDetailResponse,
  FolderEmailDetail,
  FolderEmailsResponse,
} from '@/services/email/api/emailApiTypes';

// ===== NEW UNIFIED EMAIL API =====

export const fetchEmailFolders = (includeEmpty = true, sortBy = 'count') =>
  apiClient.get<EmailFolderSummary[]>(`/emails/folders?include_empty=${includeEmpty}&sort_by=${sortBy}`);

// Unified list endpoint - supports both page-based and offset-based pagination
// plus an optional full-text search over subject/sender/body.
export const fetchEmailsInFolder = async (
  folder: string,
  limit: number = 25,
  page?: number,
  offset?: number,
  search?: string
): Promise<FolderEmailsResponse> => {
  const resolvedOffset = page !== undefined ? Math.max(0, (page - 1) * limit) : (offset || 0);
  const searchParam = search?.trim() ? `&search=${encodeURIComponent(search.trim())}` : '';
  return apiClient.get<FolderEmailsResponse>(
    `/emails/${encodeURIComponent(folder)}?limit=${limit}&offset=${resolvedOffset}${searchParam}`
  );
};

export const invalidateEmailFolderRequests = (folder: string) => {
  requestDeduplication.clearByUrl(`/emails/${encodeURIComponent(folder)}?`);
  requestDeduplication.clearByUrl('/emails/counts');
  requestDeduplication.clearByUrl('/emails/folders');
};

// Folder-scoped detail; this endpoint marks the message as read.
export const fetchEmailById = async (
  emailId: string,
  folder: string = 'inbox'
): Promise<FolderEmailDetail> => apiClient.get<FolderEmailDetail>(
  `/emails/${encodeURIComponent(folder)}/${encodeURIComponent(emailId)}`
);

// Legacy emailsys message detail (GET /api/email/messages/{id}) — reads the
// same email_messages tables. Used only as a body fallback when the
// folder-scoped detail above returns an empty body/body_html.
export const fetchLegacyEmailMessage = (emailId: string) =>
  apiClient.get<EmailMessageDetailResponse>(
    `/email/messages/${encodeURIComponent(emailId)}?expand=participants,attachments`
  );

// Auth-gated attachment download — works for both IMAP and Hostinger messages.
// The endpoint returns file bytes with Content-Disposition: attachment, but a
// plain <a href> can't carry the JWT, so we fetch as a blob and save locally.
export const getEmailAttachmentUrl = (emailId: string, attachmentId: string | number) =>
  `${apiConfig.baseUrl}/email/messages/${encodeURIComponent(emailId)}/attachments/${encodeURIComponent(String(attachmentId))}`;

export const downloadEmailAttachment = async (
  emailId: string,
  attachmentId: string | number,
  filename: string
): Promise<void> => {
  const token = localStorage.getItem('token');
  const response = await fetch(getEmailAttachmentUrl(emailId, attachmentId), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error(`Attachment download failed (${response.status})`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename || 'attachment';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

// Update message - PATCH /api/email/messages/{email_id}?is_read=...&is_starred=...&folder=...
export const updateEmailMessage = async (
  emailId: string,
  updates: {
    is_read?: boolean;
    is_starred?: boolean;
    folder?: 'inbox' | 'sent' | 'drafts' | 'trash';
  }
): Promise<{ success: true }> => {
  try {
    const params = new URLSearchParams();
    if (updates.is_read !== undefined) params.set('is_read', String(updates.is_read));
    if (updates.is_starred !== undefined) params.set('is_starred', String(updates.is_starred));
    if (updates.folder) params.set('folder', updates.folder);

    const response = await apiClient.patch<{ success: true }>(
      `/email/messages/${emailId}?${params.toString()}`
    );

    return response;
  } catch (error) {
    console.error('Error updating message:', error);
    throw error;
  }
};

// Thread endpoints
export const fetchThreads = async (
  folder: string,
  accountId?: number,
  limit: number = 25,
  offset: number = 0
) => {
  const params = new URLSearchParams({ folder, limit: String(limit), offset: String(offset) });
  if (accountId) params.set('account_id', String(accountId));
  
  return apiClient.get(`/emails/threads?${params.toString()}`);
};

export const fetchThreadMessages = async (
  threadId: string,
  limit: number = 50,
  beforeSentAt?: string,
  beforeId?: string
) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (beforeSentAt) params.set('before_sent_at', beforeSentAt);
  if (beforeId) params.set('before_id', beforeId);
  
  return apiClient.get(`/emails/threads/${threadId}?${params.toString()}`);
};

/**
 * Send a new email
 * POST /api/email/send?account_id=1&user_id=2
 */
/**
 * Send a new email
 * POST /api/email/send?account_id=1&user_id=2
 */
export const sendEmail = async (emailData: {
  to: string[];
  subject: string;
  body: string;
  body_html?: string;
  cc?: string[];
  bcc?: string[];
  from_address?: string;
  from_name?: string;
  attachments?: any[];
  // ponytail: lead context — when source='lead' + lead_id, route to the
  // live /email/send endpoint which logs a lead_activities row and BCCs
  // the sender. Non-lead sends stay on the legacy /emails/send path.
  source?: string;
  lead_id?: string;
  lead_contact_id?: string;
}, accountId?: number) => {
  try {
    console.log('📧 Sending email:', emailData);

    const query = accountId ? `?account_id=${accountId}` : '';
    // ponytail: lead-origin sends hit the endpoint that logs activities;
    // everything else is unchanged on the legacy endpoint.
    const endpoint = emailData.source === 'lead' ? '/email/send' : '/emails/send';
    const response = await apiClient.post(
      `${endpoint}${query}`,
      emailData
    );

    console.log('📧 Email sent successfully:', response);
    return response;
  } catch (error) {
    console.error('💥 Error sending email:', error);
    throw error;
  }
};

/**
 * Reply to an existing email message
 * POST /api/email/messages/{id}/reply?user_id=2
 */
export const replyToEmail = async (
  emailId: string,
  replyData: {
    body: string;
    body_html?: string;
  }
) => {
  try {
    console.log('📧 Replying to email:', emailId, replyData);
    const response = await apiClient.post(
      `/email/messages/${emailId}/reply`,
      replyData
    );
    console.log('📧 Reply sent successfully:', response);
    return response;
  } catch (error) {
    console.error('💥 Error sending reply:', error);
    throw error;
  }
};

// Draft API endpoints - Unified endpoint for creating and updating drafts
export const saveDraft = async (draftData: any, draftId?: string) => {
  try {
    const requestData = draftId 
      ? { draft_id: draftId, ...draftData }
      : draftData;
    
    console.log('💾 Saving/updating draft:', requestData);
    const response = await apiClient.post<DraftSaveResponse>('/emails/create/draft', requestData);
    console.log('💾 Draft saved successfully:', response);
    return response;
  } catch (error) {
    console.error('💥 Error saving draft:', error);
    throw error;
  }
};

// Keep updateDraft for backward compatibility - now uses the unified endpoint
export const updateDraft = async (draftId: string, draftData: any) => {
  return saveDraft(draftData, draftId);
};

export const getDraft = async (draftId: string) => {
  try {
    console.log(`🔍 Fetching draft with ID: ${draftId}`);
    const response = await apiClient.get(`/emails/drafts/${draftId}`);
    console.log(`📧 Draft response:`, response);
    return response;
  } catch (error) {
    console.error('💥 Error fetching draft:', error);
    throw error;
  }
};

export const listDrafts = async () => {
  try {
    console.log('🔍 Fetching all drafts');
    const response = await apiClient.get('/emails/drafts');
    console.log('📧 Drafts list response:', response);
    return response;
  } catch (error) {
    console.error('💥 Error fetching drafts list:', error);
    throw error;
  }
};

export const sendDraftEmail = async (draftId: string) => {
  try {
    console.log(`📧 Sending draft email with ID: ${draftId}`);
    // First get the draft
    const draft = await getDraft(draftId);
    // Then send it using the regular send endpoint
    const response = await sendEmail(draft.data);
    console.log('📧 Draft sent successfully:', response);
    return response;
  } catch (error) {
    console.error('💥 Error sending draft email:', error);
    throw error;
  }
};

// Backward compatibility wrappers using new unified endpoint
export const markEmailAsRead = async (emailId: string, folder: string = 'inbox') => {
  try {
    console.log(`✅ Marking email as read: ${emailId}`);
    const response = await apiClient.post<EmailActionResponse>(
      `/emails/${encodeURIComponent(folder)}/${encodeURIComponent(emailId)}/read`
    );
    console.log(`✅ Email marked as read:`, response);
    return response;
  } catch (error) {
    console.error('💥 Error marking email as read:', error);
    throw error;
  }
};

export const markEmailAsUnread = async (emailId: string, folder: string = 'inbox') => {
  try {
    console.log(`📧 Marking email as unread: ${emailId}`);
    const response = await apiClient.post<EmailActionResponse>(
      `/emails/${encodeURIComponent(folder)}/${encodeURIComponent(emailId)}/unread`
    );
    console.log(`✅ Email marked as unread:`, response);
    return response;
  } catch (error) {
    console.error('💥 Error marking email as unread:', error);
    throw error;
  }
};

export const deleteEmail = async (emailId: string, folder: string = 'inbox') => {
  try {
    console.log(`🗑️ Deleting email: ${emailId}`);
    const response = await apiClient.delete<EmailActionResponse>(
      `/emails/${encodeURIComponent(folder)}/${encodeURIComponent(emailId)}`
    );
    return response;
  } catch (error) {
    console.error('💥 Error deleting email:', error);
    throw error;
  }
};

export const moveEmailToTrash = async (emailId: string, fromFolder?: string) => {
  return moveEmailToFolder(emailId, fromFolder || 'inbox', 'trash');
};

export const moveEmailToFolder = (
  emailId: string,
  fromFolder: string,
  targetFolder: EmailFolderName | string
) => apiClient.post<EmailActionResponse>(
  `/emails/${encodeURIComponent(fromFolder)}/${encodeURIComponent(emailId)}/move/${encodeURIComponent(targetFolder)}`
);

// Enhanced sync with backfill support
interface SyncEmailsOptions {
  accountId?: number;
  folder?: string | 'all';
  backfill?: boolean;
  limit?: number;
  forceFull?: boolean;
}

export const syncEmailsFromServer = async (options?: SyncEmailsOptions) => {
  try {
    console.log('🔄 Syncing emails (enhanced)...', options);
    
    const params = new URLSearchParams();
    if (options?.accountId) params.set('account_id', String(options.accountId));
    if (options?.folder) params.set('folder', options.folder);
    if (options?.backfill) params.set('backfill', 'true');
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.forceFull) params.set('force_full', 'true');
    
    const endpoint = `/emails/sync${params.toString() ? '?' + params.toString() : ''}`;
    const response = await apiClient.post(endpoint);
    
    console.log('🔄 Sync scheduled:', response);
    return response;
  } catch (error: any) {
    console.error('💥 Sync error:', error);
    
    if (error.response?.status === 400 && error.response?.data?.detail?.includes('No email accounts')) {
      throw new Error('NO_EMAIL_ACCOUNTS');
    }
    
    throw error;
  }
};

// Sync status endpoint
export const fetchSyncStatus = async (accountId?: number, folder?: string) => {
  const params = new URLSearchParams();
  if (accountId) params.set('account_id', String(accountId));
  if (folder) params.set('folder', folder);
  
  return apiClient.get(`/email/sync/status${params.toString() ? '?' + params.toString() : ''}`);
};

// Email counts endpoint
export const fetchEmailCounts = async (_userId?: number, accountId?: number) => {
  const params = new URLSearchParams();
  if (accountId) params.set('account_id', String(accountId));
  
  return apiClient.get<EmailFolderSummary[]>(`/emails/counts${params.toString() ? '?' + params.toString() : ''}`);
};

export const moveDraftToSent = async (draftId: string) => {
  try {
    console.log(`📧 Moving draft ${draftId} from drafts to sent folder`);
    const response = await apiClient.post(`/email/drafts/${draftId}/move/sent`);
    console.log('📧 Draft moved to sent successfully:', response);
    return response;
  } catch (error) {
    console.error('💥 Error moving draft to sent:', error);
    throw error;
  }
};

export const fetchEmailConfig = async () => {
  try {
    console.log('📧 Fetching email configuration from API');
    const response = await apiClient.get('/email/config');
    console.log('📧 Email config response:', response);
    return response;
  } catch (error) {
    console.error('💥 Error fetching email config:', error);
    throw error;
  }
};
